import api from '@/api'
import format from '@/utils/format'
import { loadReportSignatures } from '@/utils/reportSignatures'
import {
	isPhenolTest,
	getPhenolDisplayName,
	mergePhenolCategoryRows,
	collectPhenolShowIds,
	pickLatestPhenolTestId,
	PHENOL_PRIMARY_ID,
	PHENOL_DISPLAY_NAME
} from '@/utils/pheonl'
import {
	DISPOSAL_REPORT_GROUP_IDS,
	deriveDisposalJournalResults,
	expandDisposalCategoryBySelectionOrder,
	mergeDisposalSelectionLabels,
} from '@/utils/disposalJournalDerive'

/** 0이 유효 측정값인 시험항목 — 평균 결과가 0이어도 '불검출'로 치환하지 않음
 * release 핫픽스 회수 (217819b): 중탄산/탄산·알칼리도·총대장균군 등 0 유효값 수질 항목
 */
const ZERO_VALID_TEST_IDS = [
	'WATER_TEST_080',
	'WATER_TEST_031',
	'WATER_TEST_112',
	'WATER_TEST_119',
	'WATER_TEST_183',
	'WATER_TEST_184',
	'WATER_TEST_185',
	'WATER_TEST_179',
	'WATER_TEST_180',
	'WATER_TEST_181',
	'WATER_TEST_182',
	'WATER_TEST_150',
	'WATER_TEST_151',
]

// 성적서에서 그룹 셀(시험항목 1열 병합)로 묶이는 부모: 삼성분/원소분석/물리적조성 3종
// (DISPOSAL_REPORT_GROUP_IDS 는 disposalJournalDerive 에서 export)

function averagePluralSamples(sampleList, testId = null) {
	const isPluralNo = (no) => no?.includes('\u00A0');
	const cleanNo = (no) => (no || '').replace(/[\u200B-\u200D\uFEFF\u00A0]/g, '').trim();
	const baseNoOf = (no) => cleanNo(no).replace(/-\d+$/, '');
	const parseVal = (v) => {
		if (v === '불검출') return 0;
		const n = parseFloat(v);
		return isNaN(n) ? null : n;
	};
	const getDecimalPlaces = (v) => {
		const s = String(v);
		const i = s.indexOf('.');
		return i >= 0 ? s.length - i - 1 : 0;
	};

	const isZeroValid = testId != null && ZERO_VALID_TEST_IDS.includes(testId);

	const pluralMap = {};
	sampleList.forEach((item, idx) => {
		if (isPluralNo(item.no)) {
			const bNo = baseNoOf(item.no);
			if (!pluralMap[bNo]) pluralMap[bNo] = [];
			pluralMap[bNo].push(idx);
		}
	});

	if (Object.keys(pluralMap).length === 0) return sampleList;

	for (const bNo in pluralMap) {
		const baseSample = sampleList.find(s => !isPluralNo(s.no) && cleanNo(s.no) === bNo);
		if (!baseSample) continue;

		const pluralSamples = pluralMap[bNo].map(idx => sampleList[idx]);
		const contributing = [baseSample, ...pluralSamples].map(s => s.value);
		const allValues = contributing.map(parseVal);
		const validValues = allValues.filter(v => v !== null);

		if (validValues.length >= 2) {
			// 평균 대상이 전부 문자열 '불검출'이면 zero-valid 여부와 무관하게 '불검출' 유지
			// (parseVal이 '불검출'을 0으로 변환해 실측 0과 구분되지 않는 문제 보정)
			const allUndetected =
				contributing.filter(v => v === '불검출').length === validValues.length;

			if (allUndetected) {
				baseSample.value = '불검출';
			} else {
				const maxDecimals = [baseSample, ...pluralSamples]
					.map(s => parseVal(s.value) !== null ? getDecimalPlaces(s.value) : 0)
					.reduce((a, b) => Math.max(a, b), 0);

				const avg = validValues.reduce((sum, v) => sum + v, 0) / validValues.length;
				baseSample.value = (!isZeroValid && Number(avg) === 0) ? '불검출' : avg.toFixed(maxDecimals);
			}
		}
	}

	const removeIndices = new Set(Object.values(pluralMap).flat());
	return sampleList.filter((_, idx) => !removeIndices.has(idx));
}

const state = {
	reportTitle: '',
	reportInfo: {}, // 전체 리포트에 필요한 정보
	categoryInfo: { // 시험항목 정보
		list: [],
		type: '',
	},
	combinedPages: [], // 실제 html 페이지 배열. 시료 수 + 시험 항목 수 로 결정됨. 
	sampleList: [], // 시료 정보
	isBringSample: false,
	separateArr: [],
	standardOptions: [],
	categories: [],
}

const getters = {
	type: state => state.type,
	reportInfo: state => {
		return state.reportInfo
	},
	projectInfo: state => state.projectInfo,
	categoryInfo: state => state.categoryInfo,
	sampleList: state => state.sampleList,
	testResult: state => state.testResult,
	isBringSample: state => {
		return state.reportInfo.sample?.sampleDeliveryWay?.includes('지참');
	},
	combinedPages: state => state.combinedPages,
	separateArr: state => state.separateArr,
	standardOptions: state => state.standardOptions,
	categories: state => state.categories
}

const mutations = {
	setReport: (state, data) => {
		const stripMark = s => (s || '').replace(/^[①-⑳]\s*/, '').trim();
    	const makeMark   = i => String.fromCodePoint(0x2460 + i); // ①,②…
		if(data.isChangeStandard) {
			state.reportInfo.category = [];
			state.reportInfo = {
				...state.reportInfo, 
				category: data.category.list
			}
		} else {
			const projectCategory =
				data.project?.detail?.projectCategory ?? data.category?.type ?? null
			state.reportInfo = {
				list: data.sampleList,
				category: data.category?.list ?? data.category,
				env: data.env,
				code: data.project.code,
				project: { ...data.project },
				// 표지(ReportSummary) 배출 업체 — getInfo disposal + detail 스냅샷
				disposal: data.project?.disposal ?? null,
				projectCategory,
				result: data.resultList,
				sample: data.sampleInfo,
				type: data.category.type,
				users: data.users
			}
		}
		/*시험방법(원본/표시용) 분리*/
		let rawMethods = [];
		const src = state.reportInfo?.sample?.sampleTestMethod;
		if (Array.isArray(src)) {
			rawMethods = src.map(stripMark);
		} else if (src) {
			rawMethods = src.split(',').map(stripMark);
		} else {
			rawMethods = rawMethods.filter(Boolean); // 빈 항목 제거
		}

		/** 2) 표시용 배열 생성 */
		const updateDisplayMethods = () => {
			const arr = rawMethods;
			state.reportInfo.sample.sampleTestMethod = arr.length > 1
				? arr.map((m, i) => `${makeMark(i)} ${m}`)
				: [...arr];          // 한 개면 마커 없이
		};
		updateDisplayMethods();

		// 성적서 노출 제외
		state.reportInfo.category = state.reportInfo.category.filter(el => {
			if(el.hide_type === 'hideGroup' && el.testId !== 'SOIL_TEST_016') {
				el.type = "test"
			}
			// 폐기물 일지 파생 자식(삼성분 수분/회분/가연분, Cl,S의 Cl/S 등)은
			// 시료등록 숨김(is_document_hide=1)이지만 성적서에는 노출(_derivedFrom 마킹).
			// is_document_hide 만으로 거르면 하위 항목이 사라지므로 파생 자식은 보존한다.
			if (el._derivedFrom && el.is_report_hide === 0) return true
			return el.is_document_hide === 0
		});
	
		/*보이는 테스트 ID 수집 (ModalResult docx 와 동일)*/
		const showIdsSet = collectPhenolShowIds(state.reportInfo.list);
		const showIds = [...showIdsSet];
		const latestPhenolTestId = data.latestPhenolTestId ?? state.reportInfo.latestPhenolTestId ?? null;
		if (latestPhenolTestId != null) {
			state.reportInfo.latestPhenolTestId = latestPhenolTestId;
		}


		const matchMethodToNum = (item) => {
			//토양, 수질 모두 번호로
			if (!showIds.includes(item.testId)) return '';
	
			// 1) 문자열 정규화
			const str = item.report_method?.replaceAll('제 ', '제').trim() || '';
	
			// 3) rawMethods 검색
			let idx = rawMethods.findIndex(m => m.includes(str));
			if (idx === -1) {
				rawMethods.push(str);        // 원본에 추가
				updateDisplayMethods();      // 전체 재번호
				idx = rawMethods.length - 1;
			}
			return String.fromCodePoint(0x2460 + idx); // ①,②…
		};

		//측정기기명 가져오기
		const reformatMethodText = (item) => {
			if (!item.method) return '';
			
			const trimmed = item.method.trim();
			const toHtml = (text) => text.trim().replace(/\n/g, '<br>');
			
			// 괄호 안 내용 추출 또는 전체 텍스트 사용
			const match = trimmed.match(/\(([^)]*)\)/);
			const methodText = toHtml(match ? match[1] : trimmed);
			
			if (!methodText) return '';
			
			const deviceText = item.device ? `<br><em>(${item.device})</em>` : '';
			return `<em>${methodText}</em>${deviceText}`;
		}

		// displayName 변환 함수 (항목명 표시용)
		const subIds = ['WATER_TEST_076', 'WATER_TEST_114'];
		const subIds2 = ['WATER_TEST_077']
		
		// 시험항목 표시명:
		// - WATER: name, DISPOSAL: search_name 우선, 그 외: lab_name 우선
		const itemLabel = (item) => {
			if (state.type === 'WATER') return item?.name || '';
			if (state.type === 'DISPOSAL') {
				return item?.search_name || item?.name || item?.lab_name || '';
			}
			return item?.lab_name?.trim() || item?.name || '';
		};

		// 공통 변환 (ReportPortrait용)
		const formatDisplayName = (item) => {
			const label = itemLabel(item);
			if (isPhenolTest(item.testId, state.type)) return getPhenolDisplayName(item.testId, label, state.type);
			if (subIds.includes(item.testId)) {
				return 'COD<sub>Mn</sub>';
			}
			if (subIds2.includes(item.testId)) {
				return 'COD<sub>Cr</sub>';
			}
			return label;
		};
		
		// 수질 전용 변환 (ReportWater용)
		const formatWaterDisplayName = (item) => {
			const label = itemLabel(item);
			if (isPhenolTest(item.testId, 'WATER')) return getPhenolDisplayName(item.testId, label, 'WATER');
			const firstParenthesisIds = ['WATER_TEST_074', 'WATER_TEST_075'];
			const subIds2 = ['WATER_TEST_077']
			const lastParenthesisIds = ['WATER_TEST_009'];
			
			if (subIds.includes(item.testId)) {
				return 'COD<sub>Mn</sub>';
			} else if (subIds2.includes(item.testId)) {
				return 'COD<sub>Cr</sub>';
			} else if (firstParenthesisIds.includes(item.testId)) {
				const idx = label.indexOf('(');
				if (idx > -1) {
					return `${label.slice(0, idx)}<br>(${label.slice(idx + 1)}`;
				}
			} else if (lastParenthesisIds.includes(item.testId)) {
				const idx = label.lastIndexOf(')');
				if (idx > -1) {
					return `${label.slice(0, idx + 1)}<br>${label.slice(idx + 1)}`;
				}
			}
			return label;
		};

		//unit, method 값 매칭해주기
		state.reportInfo.category = state.reportInfo.category.map(el => {
			el.init_name = itemLabel(el);
			el.displayName = formatDisplayName(el);
			el.waterDisplayName = formatWaterDisplayName(el);
			// 성적서 항목명 수기 수정값(customName)은 «여기서 선언하지 않는다».
			//
			// 이 객체들은 state.reportInfo 가 관찰(observe)된 «뒤에» map 으로 가공되므로,
			// 여기서 붙인 속성은 반응형이 되지 않는다(displayName 도 마찬가지로 평범한 속성이다).
			// 그 상태에서 customName 을 미리 null 로 만들어 두면, 편집 시 호출하는
			// Vue.set 이 `key in target` 분기를 타 «그냥 대입»만 하고 notify 를 하지 않는다.
			// → 1페이지에서 고쳐도 뒷페이지가 안 따라온다. (2026-08-31 운영 확인)
			//
			// 키가 없는 상태로 두면 Vue.set 이 defineReactive + ob.dep.notify() 를 수행하고,
			// 렌더 워처는 dependArray 로 각 항목 객체의 __ob__.dep 를 이미 구독하고 있어
			// 전 페이지가 함께 갱신된다. 표시값은 resolveItemName 이 undefined 를
			// «수정 안 함»으로 처리하므로 초기값 선언이 필요 없다.
			// 성적서 그룹 셀: SOIL 은 BTEX/페놀 고정, DISPOSAL 은 그룹 대상(삼성분/원소분석/물리적조성)의
			// 자식(hide_parent_id 또는 parent_id)에 그룹명 부여. ReportPortrait 는 groupKey 로 rowspan 계산.
			const disposalGroupParent = el.hide_parent_id || el.parent_id;
			if(el.parent_id === 'SOIL_TEST_040' || el.parent_id === 'SOIL_TEST_016') {
				const result = state.categoryInfo.list.filter(item => item.testId == el.parent_id);
				if(result) {
					el.groupName = itemLabel(result[0]);
					el.groupKey = el.parent_id;
				}
			} else if(state.type === 'DISPOSAL' && DISPOSAL_REPORT_GROUP_IDS.includes(disposalGroupParent)) {
				const result = state.categoryInfo.list.filter(item => item.testId == disposalGroupParent);
				if(result.length) {
					el.groupName = itemLabel(result[0]);
					el.groupKey = disposalGroupParent;
				}
			}
			state.reportInfo.result.forEach(item => {
				if(item.testId === el.testId) {
					el.method = item.testMethod ? item.testMethod : el.method?.split(',')[0];
					el.machine = item.testMachine ? item.testMachine : el.machine;
					el.unit = item.testResultUnit;
					el.list = item.sampleList.map(sub => {
						//자일렌일 경우(SOIL_TEST_068) 0.0 시 텍스트 불검출로 노출
						if((el.testId === 'SOIL_TEST_068' || el.testId === 'WATER_TEST_145') && sub.value === '0.0') {
							sub.value = '불검출'
						}
						return sub
					})
					el.list = averagePluralSamples(el.list, el.testId)
				}
			})
			el.report_method = state.type === 'DISPOSAL' ? '' : matchMethodToNum(el);
			//반띄움 기능 추가
			el.criteria_area_1 = format.makeHalfSpacing(el.criteria_area_1);
			el.criteria_area_2 = format.makeHalfSpacing(el.criteria_area_2);
			el.criteria_area_3 = format.makeHalfSpacing(el.criteria_area_3);
			el.method = reformatMethodText(el);
			// console.log(el.method, el.testId, el.name)
			return el;
		})
		// 페놀 027/102 → 한 행 + ModalResult와 같은 측정값 병합
		state.reportInfo.category = mergePhenolCategoryRows(state.reportInfo.category, state.type, {
			latestPhenolTestId,
			showIds: showIdsSet
		});
		state.reportInfo.sample.sampleCollectorOpinion = state.reportInfo.sample?.sampleDeliveryWay?.includes('지참') ? '정상가동' : '-';
	},	
	setData: (state, data) => {
		state.categoryInfo = data.category;
		state.sampleList = data.sampleList;
		state.type = data.category.type;
	},
	setCombinedPages(state, data) {
		const { listSize, categorySize, detail } = data;
		const info = detail || { ...state.reportInfo };
		const reportCode = state.reportInfo.code;
		
		// 공통: 페이지별 시료 분리 및 카테고리 페이지 생성
		const listPages = chunkArray(info.list, listSize);
		const categoryPages = createCategoryPages(info, categorySize);
		
		// 공통 헬퍼: 시료의 글로벌 인덱스 찾기
		const findGlobalIndex = (sample) => info.list.findIndex(x => x.sampleId === sample.sampleId);

		/**
		 * 성적서에 인쇄할 시료 번호(표시 전용).
		 *
		 * 분리발행이면 그 성적서 안에서 1부터 다시 매긴다.
		 * 예) 시료 1~5 를 (1,2,3) / (4,5) 로 분리 → 두 번째 성적서는 1,2 로 인쇄.
		 *
		 * ⚠ sampleNo 자체는 절대 바꾸지 않는다. sampleNo 는 화면 표시값이면서
		 *    동시에 측정값을 찾아오는 키다(ReportLandscape·ReportPortrait 의
		 *    `cat.list.find(el => el.no === item.sampleNo)`). 번호를 다시 매기면
		 *    4·5번 시료의 값이 엉뚱하게 붙거나 비어버린다.
		 *
		 * 전체발행에는 적용하지 않는다. 중간 시료가 삭제되어 no 가 1,2,4,5 인
		 * 업무에서 1,2,3,4 로 찍히면 기존 발행본과 어긋나기 때문이다.
		 */
		const isSeparate = !!data.isSeparate;
		const displayNoOf = (sample) => {
			if (!isSeparate) return sample.sampleNo;
			const idx = findGlobalIndex(sample);
			return idx < 0 ? sample.sampleNo : idx + 1;
		};

		// 공통 헬퍼: 시료에 페이지 정보 추가
		const addPageInfo = (sample, pageIndex) => ({
			...sample,
			index: pageIndex,
			displayNo: displayNoOf(sample),
			title: `${reportCode}-${pageIndex + 1}`
		});
		
		// 타입별 페이지 생성 전략
		const pageGenerators = {
			// WATER: 각 시료별로 독립적인 카테고리 순서 유지
			WATER: () => {
				const result = [];
				listPages.forEach((currentListSlice, pageIndex) => {
					currentListSlice.forEach((sample) => {
						const globalSampleIdx = findGlobalIndex(sample);
						const catPagesOfSample = categoryPages[globalSampleIdx] || [];
						
						catPagesOfSample.forEach((catChunk, catPageIdx) => {
							result.push({
								categoryPage: catChunk,
								listPage: [addPageInfo(sample, pageIndex)],
								sampleIndex: globalSampleIdx,
								categoryPageIndex: catPageIdx
							});
						});
					});
				});
				
				// state.categories: 첫 번째 시료의 전체 카테고리
				state.categories = categoryPages[0]?.flat() || [];
				return result;
			},
			
			// SOIL: 모든 시료의 카테고리 합쳐서 doc_order_no 정렬
			SOIL: () => {
				const result = [];
				// 분리발행 포함 항목명 등: 시료 페이지와 무관하게 전 시료 시험항목 합집합 유지
				state.categories = removeDuplicatesByKey(
					categoryPages.flatMap(pages => pages.flat()),
					'testId'
				).sort((a, b) => a.doc_order_no - b.doc_order_no);

				listPages.forEach((currentListSlice, pageIndex) => {
					const sampleIndices = currentListSlice.map(findGlobalIndex);
					
					// 카테고리 수집
					let catAll = sampleIndices.flatMap(sampleIdx => 
						(categoryPages[sampleIdx] || []).flat()
					);
					
					// 가로형, 행열미변경에서 시험 항목 일관성 있도록 수정
					if (listSize === 14 && categorySize === 24) {
						catAll = categoryPages.flatMap(pages => pages.flat());
					}
					
					// testId 기준 중복 제거 후 doc_order_no 정렬
					const catUnique = removeDuplicatesByKey(catAll, 'testId')
						.sort((a, b) => a.doc_order_no - b.doc_order_no);
					
					// 청크 분할 후 결과 생성
					chunkArray(catUnique, categorySize).forEach(catChunk => {
						result.push({
							categoryPage: catChunk,
							listPage: currentListSlice.map(sample => addPageInfo(sample, pageIndex))
						});
					});
				});
				return result;
			},

			// DISPOSAL: 페이지 구조(다시료 열)는 SOIL 과 같지만, 항목 순서는 수질처럼
			// 사용자 선택 순서를 보존한다 — doc_order_no 재정렬 없이 등장 순서대로 중복 제거.
			DISPOSAL: () => {
				const result = [];
				state.categories = removeDuplicatesByKey(
					categoryPages.flatMap(pages => pages.flat()),
					'testId'
				);

				listPages.forEach((currentListSlice, pageIndex) => {
					const sampleIndices = currentListSlice.map(findGlobalIndex);

					let catAll = sampleIndices.flatMap(sampleIdx =>
						(categoryPages[sampleIdx] || []).flat()
					);

					// 가로형, 행열미변경에서 시험 항목 일관성 있도록 수정 (SOIL 과 동일 예외)
					if (listSize === 14 && categorySize === 24) {
						catAll = categoryPages.flatMap(pages => pages.flat());
					}

					const catUnique = removeDuplicatesByKey(catAll, 'testId');

					chunkArray(catUnique, categorySize).forEach(catChunk => {
						result.push({
							categoryPage: catChunk,
							listPage: currentListSlice.map(sample => addPageInfo(sample, pageIndex))
						});
					});
				});
				return result;
			}
		};
		
		// 타입에 따른 페이지 생성 실행
		const generator = pageGenerators[info.type];
		state.combinedPages = generator ? generator() : [];
	},
	setSeparateArr(state, data) {
		//분리 발행 목록 배열
		//임시저장용이기 때문에 modal 처음 보일 때에는 reset
		if(data) {
			// 뒤에 붙인다. 예전에는 앞에 붙여(unshift) 목록이 `-2, -1, 원본` 처럼
			// 번호와 반대 순서로 보였고, deleteSeparate 의 기본 선택(separateArr[0])도
			// 원본이 아니라 마지막에 만든 분리본을 가리켰다.
			// 이제 `원본, -1, -2, …` 순으로 쌓여 화면 표시와 번호가 일치한다.
			state.separateArr = [...state.separateArr, JSON.parse(JSON.stringify(data))]
		} else {
			state.separateArr = []
		}
	},
	setStandardOptions(state, data) {
		state.standardOptions = [...data]
	}
}


// 배열을 chunkSize 단위로 분할하는 헬퍼 함수
function chunkArray(arr, chunkSize) {
	const pageCount = Math.ceil(arr.length / chunkSize);
	return Array.from({ length: pageCount }, (_, i) => 
		arr.slice(i * chunkSize, (i + 1) * chunkSize)
	);
}

// 특정 키 기준으로 중복 제거하는 헬퍼 함수
function removeDuplicatesByKey(arr, key) {
	const map = new Map();
	arr.forEach(item => {
		if (!map.has(item[key])) {
			map.set(item[key], item);
		}
	});
	return Array.from(map.values());
}

// 타입별 카테고리 추출 전략
const categoryExtractors = {
	// SOIL: testResult에서 isTest가 true인 항목 필터링
	SOIL: (sample, infoCategory) => {
		const showIds = sample.testResult 
			? Object.keys(sample.testResult).filter(key => sample.testResult[key].isTest)
			: [];
		return infoCategory.filter(cat => showIds.includes(cat.testId) && cat.type !== 'group');
	},

	// DISPOSAL: 결과확인창(ModalResult)과 동일하게 "전 시료 병합 선택순서"를 따른다.
	//  - 순서 소스: mergeDisposalSelectionLabels(전 시료 categories 병합) → 모든 시료 페이지가 화면과 동일 순서
	//    (mergedLabels 미제공/빈 값이면 시료별 sample.categories 로 폴백)
	//  - 선택 항목이 일지 파생 부모(_hideInReport: 삼성분/원소분석/물리적조성)면 그 자리에서 파생 행으로 전개
	//  - 이름 매칭 실패/누락 항목은 testResult.isTest 기반으로 뒤에 보완(WATER 와 동일한 안전망)
	DISPOSAL: (sample, infoCategory, mergedLabels) => {
		const showIds = sample.testResult
			? Object.keys(sample.testResult).filter(key => sample.testResult[key]?.isTest)
			: [];
		// 원소분석 등: 자식만 선택돼도 부모 슬롯 전개가 가능하도록 파생 부모 id 포함
		const showWithParents = new Set(showIds);
		infoCategory.forEach(cat => {
			if (cat?._derivedFrom && showWithParents.has(cat.testId)) {
				showWithParents.add(cat._derivedFrom);
			}
		});
		let categories = Array.isArray(mergedLabels) && mergedLabels.length ? mergedLabels : sample?.categories;
		if (typeof categories === 'string') {
			try { categories = JSON.parse(categories); } catch { categories = []; }
		}
		return expandDisposalCategoryBySelectionOrder(infoCategory, {
			categories: Array.isArray(categories) ? categories : [],
			showIds: showWithParents,
			enforceDocumentHide: false,
		});
	},
	
	// WATER: 1차로 sample.categories 의 순서를 그대로 유지(이름 매칭, name·init_name 기준)하고,
	//        2차로 sample.testResult.isTest === true 인데 1차에서 누락된 항목을 testId 기반으로 보완한다.
	//        (sample.categories 와 testResult 가 비동기로 갱신되거나, 카테고리 이름 표기가 미세하게
	//         달라 이름 매칭에 실패해 화면에서 누락되던 문제를 방지)
	//        단, 다른 항목이 parent_id 로 참조하는 그룹 항목(cat.type === 'group') 은 제외 — SOIL 추출기와 동일.
	WATER: (sample, infoCategory) => {
		const phenolItem = infoCategory.find(cat => cat.testId === PHENOL_PRIMARY_ID);
		const result = [];
		const usedIds = new Set();
		const phenolSeen = { used: false };

		// 그룹(다른 항목이 parent_id 로 참조) 항목은 시험성적서에 표시 대상 아님
		const isGroup = (cat) => cat?.type === 'group';

		const pushUnique = (cat) => {
			if (!cat || !cat.testId || usedIds.has(cat.testId)) return;
			if (isGroup(cat)) return;
			result.push(cat);
			usedIds.add(cat.testId);
		};

		// 1) sample.categories 우선 적용 — 기존 사용자 정의 순서를 그대로 유지
		if (Array.isArray(sample?.categories)) {
			sample.categories.forEach(categoryName => {
				const found = infoCategory.find(cat =>
					cat.name === categoryName ||
					cat.init_name === categoryName
				);
				if (found) {
					pushUnique(found);
					return;
				}
				if (phenolItem && typeof categoryName === 'string' && categoryName.includes(PHENOL_DISPLAY_NAME)) {
					if (phenolSeen.used) return;
					phenolSeen.used = true;
					pushUnique(phenolItem);
				}
			});
		}

		// 2) testResult.isTest=true 인데 1차에 누락된 항목 보완 — testId 기반 매칭, infoCategory 자연순서 유지
		//    페놀류(WATER_TEST_027 / WATER_TEST_102)는 PRIMARY(027) 한 행으로 정규화하여 중복을 막는다.
		const isTestIds = sample?.testResult
			? Object.keys(sample.testResult).filter(key => sample.testResult[key]?.isTest)
			: [];
		const normalizedIsTestIds = new Set(
			isTestIds.map(id => isPhenolTest(id, 'WATER') ? PHENOL_PRIMARY_ID : id)
		);

		infoCategory.forEach(cat => {
			if (!cat?.testId) return;
			if (!normalizedIsTestIds.has(cat.testId)) return;
			pushUnique(cat);
		});

		return result;
	}
};

function createCategoryPages(info, categorySize) {
	const extractor = categoryExtractors[info.type];
	
	if (!extractor) {
		console.warn(`Unknown type: ${info.type}`);
		return [];
	}
	
	// DISPOSAL: 화면(ModalResult)과 동일한 전 시료 병합 선택순서를 모든 시료 페이지에 공통 적용
	const mergedDisposalLabels = info.type === 'DISPOSAL'
		? mergeDisposalSelectionLabels(info.list)
		: null;
	
	// 각 샘플별 카테고리 배열 생성
	const categories = info.list.map(sample => extractor(sample, info.category, mergedDisposalLabels));
	
	// 각 샘플의 카테고리 배열을 categorySize 단위로 청크 분할
	return categories.map(catArr => chunkArray(catArr, categorySize));
}	

const actions = {
	loadReportInfo: async ({commit, dispatch}, projectId) => {
		//프로젝트 상세 정보
		const project = await api.project.getInfo(projectId)
		//시험항목(카테고리) 정보
		// - SOIL: 성적서 순서(doc_order_no) 정렬
		// - DISPOSAL: 결과확인창(ModalResult)과 동일하게 result_order_no 기준으로 fetch
		//   (선택순서 미매칭 잔여 항목의 기준정렬을 화면과 일치시키기 위함)
		const disposalOrSoilOrder =
			project.detail.projectCategory === 'SOIL'
				? 'doc'
				: project.detail.projectCategory === 'DISPOSAL'
					? 'result'
					: null
		const category = await api.project.getCategory(projectId, null, disposalOrSoilOrder)

		//시료 리스트
		const sampleList = await api.project.getSampleList(projectId, false, true)
		//시료 정보
		const sampleInfo = await api.project.getSample(projectId)
		//시험 결과
		const resultList = await api.project.getTestResult(projectId);
		//폐기물: 삼성분/물리적조성 성적서 행·결과를 시험일지 결과에서 파생 (삼성분 소계 포함)
		if (project.detail.projectCategory === 'DISPOSAL') {
			await deriveDisposalJournalResults(projectId, category, resultList, sampleList, {
				includeSubtotal: true,
			})
		}
		// 페놀 최신 시험방법 (ModalResult docx.loadTestResult 와 동일)
		const reportRes = await api.project.getReport(projectId);
		const latestPhenolTestId = pickLatestPhenolTestId(reportRes?.list);
		//시험 환경
		const env = await api.project.getTestResultInfo(projectId);
		//시료 전체 정보 가

		const userKeys = ['sampleCollector', 'master', 'researcher'];
		const reportUsers = {};

		//시료채취자, 시험자, 책임자 이미지 가져오기
		for(let i = 0; i < userKeys.length; i++) {
			const key = userKeys[i]
			reportUsers[key] = await api.user.getTestUserInfo(projectId, key);
		}

		//접수자 정보 가져오기
		reportUsers.projectUser = {
			id: project.detail.projectUserId,
			name: project.detail.projectUser,
		}
		const users = await loadReportSignatures(reportUsers, id => api.user.getUserSignImg(id))

		//시험 항목, 시료 리스트는 가장 기본 형태로 가지고 있어야 하기 때문에 초기에 따로 저장
		await commit('setData', {sampleList: sampleList, category: category})
		await commit('setReport', {
			project: project, 
			category: category,
			sampleList: sampleList, 
			sampleInfo: sampleInfo, 
			resultList: resultList, 
			env: env, 
			users: users,
			latestPhenolTestId
		})
	},
	refreshReportSignatures: async (context, users) => {
		return loadReportSignatures(users, id => api.user.getUserSignImg(id))
	},
	getCombinedPages: async ({commit, dispatch}, payload) => {
		commit('setCombinedPages', payload)
	},
	getStandardOptions: async({commit}, type) => {
		const res = await api.project.getStandardList(type);
		await commit('setStandardOptions', res.list)
	},
	changeStandardData: async({commit, state}, payload) => {
		const { projectId, date } = payload
		const res = await api.project.getCategory(projectId, date)
		await commit('setReport', {category: res, isChangeStandard: true})
	}
}

export default { namespaced: true, state, getters, mutations, actions }
